import { z } from 'zod';

/**
 * Copied from Storifyme docs: https://docs.storifyme.com/api#/operations/getSnapById
 *
 * NOTE: does not include all properties, just the ones we interact with
 * */
export type StorifymeSnap = {
  id: number;
  name: string;
  url: string;
  original_poster: string; // This is the story image
  updated_at: Date;
  configuration?: {
    pages?: { id: string; name: string; [key: string]: any }[];
    [key: string]: any;
  }; // Not provided explicitly by storifyme
};

/** Get Account Snap SDK Credentials to use in the UI */
export type GetAccountSnapSdkCredentialsRequestParams = {
  accountId: string;
};

/**
 * The `accountId` and `sdkKey` are used to authenticate with the Storifyme Web SDK
 */
export type GetAccountSnapSdkCredentialsResponseBody = {
  // The `accountId` in storifyme
  accountId: string;
  sdkKey: string;
};

export type GetAccountSDKTokenResponseBody = {
  token: string;
  msUntilExpiration: number;
};

/** Capture Storifyme Snap Event */
export type CaptureStorifymeSnapEventRequestParams = {
  /**
   * Sparkplug accountId
   */
  accountId: string;
};
export type CaptureStorifymeSnapEventRequestQueryParams = {
  /**
   * The event type
   */
  action: string;

  /**
   * The snap id
   */
  story_id: string;

  /**
   * The StorifyMe account id
   */
  account_id: string;

  /**
   * The userId
   */
  cid?: string;

  /**
   * The sparkId
   */
  pid?: string;

  /**
   * There are other properties that can be sent to StorifyMe, but we don't know what they are yet
   */
  [key: string]: string | undefined;
};
export type CaptureStorifymeSnapEventResponseBody = void;

export type SparkSnap = {
  id: number;
  name: string;
  posterImg: string;
  spark: {
    _id: string;
    name: string;
    startDate: string;
    endDate: string;
    retailerAccountId: string;
    retailer: {
      name: string;
    };
    sparkBrandId: string;
    sparkBrand: {
      name: string;
    };
  };
  /**
   * The number of times the snap has been viewed. This is currently `undefined` because we
   * can't easily capture this data from Storifymy yet. This data is pending the implementation
   * of their data tracking API.
   */
  viewCount?: number;
  /**
   * The average read time for the snap. This is currently `undefined` because we
   * can't easily capture this data from Storifymy yet. This data is pending the implementation
   * of their data tracking API.
   */
  avgReadTime?: number;
};

/** List Snaps */
export type ListAccountSnapsRequestParams = {
  accountId: string;
};
export type ListAccountSnapsResponseBody = {
  snaps: SparkSnap[];
};

/** Get Snap */
export type StorifyMeThumbnailStatus = 'ready' | 'failed';
export type GetAccountSnapRequestParams = {
  accountId: string;
  snapId: string;
};
export type GetAccountSnapResponseBody = {
  snap: StorifymeSnap;
  thumbnailStatus: StorifyMeThumbnailStatus;
  thumbnailUrl: string;
};

/** Update Snap */
export type UpdateAccountSnapRequestParams = {
  accountId: string;
  snapId: string;
};
export const updateAccountSnapRequestBodySchema = z.object({
  name: z.string().optional(),
});
export type UpdateAccountSnapRequestBody = z.infer<typeof updateAccountSnapRequestBodySchema>;
export type UpdateAccountSnapResponseBody = void;

/**
 * Copied from email with Storifyme. This needs to be expanded based on what we discover in the network tab
 * */
export type StorifymeSnapTemplate = {
  id: number;
  name: string;
  preview_img: string;
  updated_at: Date;
};

export type PublicSnapTemplate = {
  id: number;
  sourceSnapId: number;
  name: string;
  previewImg: string;
  previewImgStatus?: StorifyMeThumbnailStatus;
  slideCount: number;
  configuration: any;
  updated_at: Date;
  /** StorifyMe uses the raw template data to load templates into the editor */
  rawStorifyMeTemplate: any;
};

/** List Account Snap Templates */
export type ListAccountSnapTemplatesRequestParams = {
  accountId: string;
};
export type ListAccountSnapTemplatesResponseBody = {
  snapTemplates: PublicSnapTemplate[];
};

/** Global templates are available across all accounts */
export type ListRawGlobalSnapTemplatesResponseBody = {
  globalTemplates: StorifymeSnapTemplate[];
};

export type GetAccountSnapTemplateRequestParams = {
  accountId: string;
  templateId: string;
};
export type GetAccountSnapTemplateResponseBody = {
  snapTemplate: PublicSnapTemplate;
};

/** Create Account Snap Template */
export type CreateAccountSnapTemplateRequestParams = {
  accountId: string;
};
export const createAccountSnapTemplateRequestBodySchema = z.object({
  snapId: z.number(),
});
export type CreateAccountSnapTemplateRequestBody = z.infer<
  typeof createAccountSnapTemplateRequestBodySchema
>;
export type CreateAccountSnapTemplateResponseBody = void;

/** Update Account Snap Template */
export type UpdateAccountSnapTemplateRequestParams = {
  accountId: string;
  snapTemplateId: string;
};
export const updateAccountSnapTemplateRequestBodySchema = z.object({
  snapId: z.number(),
});
export type UpdateAccountSnapTemplateRequestBody = z.infer<
  typeof updateAccountSnapTemplateRequestBodySchema
>;
export type UpdateAccountSnapTemplateResponseBody = void;

/** Update Account Snap Template */
export type DeleteAccountSnapTemplateRequestParams = {
  accountId: string;
  snapTemplateId: string;
};
export type DeleteAccountSnapTemplateResponseBody = void;
